import React, { useState, useEffect, useCallback } from "react";

import * as Yup from "yup";
import { Formik } from "formik";
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Divider,
} from "@mui/material";
import axios from "axios";

import config from "../../consts";

import useAuth from "../../hooks/useAuth";
import useIsMountedRef from "../../hooks/useIsMountedRef";
import PasswordInput from "@rlmonger/web_components/dist/Inputs/Password";
import TOTPEntryDialog from "./TOTPEntryDialog";

const MS_SSO_STATE_KEY = "ms_sso_state";
const MS_SSO_NONCE_KEY = "ms_sso_nonce";
const IS_PRODUCTION = process.env.NODE_ENV === "production";
const MICROSOFT_SIGN_IN_ERROR_MESSAGE =
  "We couldn't sign you in with Microsoft. Please try again or use your email and password. If the problem continues, contact your administrator.";
const MICROSOFT_SIGN_IN_UNAVAILABLE_MESSAGE =
  "Microsoft sign-in is currently unavailable. Please use your email and password or contact your administrator.";

const getMicrosoftErrorMessage = (developmentMessage, productionMessage) =>
  IS_PRODUCTION ? productionMessage : developmentMessage;

const decodeBase64Url = (value) => {
  if (!value) return null;

  const normalised = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalised.padEnd(
    normalised.length + ((4 - (normalised.length % 4)) % 4),
    "=",
  );

  return atob(padded);
};

const decodeJwtPayload = (token) => {
  if (typeof token !== "string") return null;

  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    return JSON.parse(decodeBase64Url(payload));
  } catch (error) {
    return null;
  }
};

const randomString = (length = 40) => {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);

  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(bytes)
    .map((value) => alphabet[value % alphabet.length])
    .join("");
};

const clearMicrosoftHash = () => {
  const cleanUrl = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState({}, document.title, cleanUrl);
};

const buildMicrosoftAuthorizeUrl = ({
  clientId,
  tenantId,
  redirectUri,
  state,
  nonce,
}) => {
  const authority = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`;
  const params = new URLSearchParams({
    client_id: clientId,
    response_mode: "fragment",
    response_type: "id_token",
    scope: "openid profile email",
    redirect_uri: redirectUri,
    state,
    nonce,
    prompt: "select_account",
  });

  return `${authority}?${params.toString()}`;
};

const LoginJWT = (props) => {
  const isMountedRef = useIsMountedRef();
  const { login, loginWithMicrosoft, twoFactorLogin } = useAuth();

  const [forgotEmail, setForgotEmail] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);

  const [twoFactorUserID, setTwoFactorUserID] = useState(null);
  const [OTPOpen, setOTPOpen] = useState(false);
  const [OTPError, setOTPError] = useState(null);
  const [OTPLoading, setOTPLoading] = useState(false);

  const microsoftClientId = process.env.REACT_APP_MS_SSO_CLIENT_ID;
  const microsoftTenantId = process.env.REACT_APP_MS_SSO_TENANT_ID;
  const microsoftRedirectUri =
    process.env.REACT_APP_MS_SSO_REDIRECT_URI ||
    `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const microsoftConfigured = Boolean(
    microsoftClientId && microsoftTenantId,
  );

  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length <= 1) return undefined;

    const params = new URLSearchParams(hash.slice(1));
    const microsoftToken = params.get("id_token");
    const providerError = params.get("error");
    const providerErrorDescription = params.get("error_description");

    if (!microsoftToken && !providerError) return undefined;

    clearMicrosoftHash();

    if (providerError) {
      const message = decodeURIComponent(
        (providerErrorDescription || providerError).replace(/\+/g, " "),
      );

      setAlert({
        severity: "error",
        message: getMicrosoftErrorMessage(
          `Microsoft login failed: ${message}`,
          MICROSOFT_SIGN_IN_ERROR_MESSAGE,
        ),
      });

      return undefined;
    }

    const expectedState = sessionStorage.getItem(MS_SSO_STATE_KEY);
    const expectedNonce = sessionStorage.getItem(MS_SSO_NONCE_KEY);
    const state = params.get("state");
    const payload = decodeJwtPayload(microsoftToken);

    sessionStorage.removeItem(MS_SSO_STATE_KEY);
    sessionStorage.removeItem(MS_SSO_NONCE_KEY);

    if (!expectedState || state !== expectedState) {
      setAlert({
        severity: "error",
        message: getMicrosoftErrorMessage(
          "Microsoft login failed. Sign-in state mismatch, please try again.",
          MICROSOFT_SIGN_IN_ERROR_MESSAGE,
        ),
      });

      return undefined;
    }

    if (!payload || !expectedNonce || payload.nonce !== expectedNonce) {
      setAlert({
        severity: "error",
        message: getMicrosoftErrorMessage(
          "Microsoft login failed. Sign-in nonce mismatch, please try again.",
          MICROSOFT_SIGN_IN_ERROR_MESSAGE,
        ),
      });

      return undefined;
    }

    let cancelled = false;
    setMicrosoftLoading(true);

    loginWithMicrosoft(microsoftToken)
      .catch((err) => {
        if (!cancelled) {
          setAlert({
            severity: "error",
            message: getMicrosoftErrorMessage(
              err.message || "Microsoft login failed. Please try again.",
              MICROSOFT_SIGN_IN_ERROR_MESSAGE,
            ),
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMicrosoftLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loginWithMicrosoft]);

  const handleOTPSubmit = async (otp) => {
    try {
      setOTPLoading(true);
      setOTPError(null);

      const { data } = await axios.post(`${config.api}/auth/2fa-verify`, {
        otp,
        user_id: twoFactorUserID,
      });

      if (data.error) {
        setOTPError(data.error);
        return;
      }

      let { token, user } = data;

      await twoFactorLogin(token, user);

      setTwoFactorUserID(null);
      handleOTPClose();
    } catch (error) {
      setOTPError("Unable to verify the code. Please try again.");
    } finally {
      setOTPLoading(false);
    }
  };

  const handleOTPOpen = () => {
    setOTPOpen(true);
  };

  const handleOTPClose = () => {
    setOTPOpen(false);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const getRecoveryEmail = async () => {
    try {
      setLoading(true);

      await axios.post(`${config.api}/auth/forgotPassword`, {
        email: forgotEmail,
        platform: config.platform,
      });

      setAlert({
        severity: "success",
        message: "Password reset email sent successfully.",
      });
      setForgotEmail("");
      setModalOpen(false);
    } catch (error) {
      setAlert({
        severity: "error",
        message:
          "Something went wrong whilst requesting a password reset. Email address might not be associated with an account.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = useCallback(() => {
    if (!microsoftConfigured) {
      setAlert({
        severity: "error",
        message: getMicrosoftErrorMessage(
          "Microsoft SSO is not configured for this environment. Ask an admin to set REACT_APP_MS_SSO_CLIENT_ID and REACT_APP_MS_SSO_TENANT_ID.",
          MICROSOFT_SIGN_IN_UNAVAILABLE_MESSAGE,
        ),
      });
      return;
    }

    const state = randomString(48);
    const nonce = randomString(48);
    sessionStorage.setItem(MS_SSO_STATE_KEY, state);
    sessionStorage.setItem(MS_SSO_NONCE_KEY, nonce);

    const url = buildMicrosoftAuthorizeUrl({
      clientId: microsoftClientId,
      tenantId: microsoftTenantId,
      redirectUri: microsoftRedirectUri,
      state,
      nonce,
    });

    setMicrosoftLoading(true);
    window.location.assign(url);
  }, [
    microsoftClientId,
    microsoftConfigured,
    microsoftRedirectUri,
    microsoftTenantId,
  ]);

  return (
    <>
      <Formik
        initialValues={{
          email: "",
          password: "",
          submit: null,
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Must be a valid email")
            .max(255)
            .required("Email is required"),
          password: Yup.string().max(255).required("Password is required"),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            setAlert(null);
            const loginResult = await login(
              values.email,
              values.password,
            );
            const user_id = loginResult?.user_id;
            const code = loginResult?.code;

            if (code === 2) {
              setTwoFactorUserID(user_id);
              handleOTPOpen();
            }
            if (isMountedRef.current) {
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err) {
            setAlert({
              severity: "error",
              message: err.message,
            });
            if (isMountedRef.current) {
              setStatus({ success: false });
              setErrors({ submit: err.message });
              setSubmitting(false);
            }
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form
            style={{ width: `100%` }}
            noValidate
            onSubmit={handleSubmit}
            {...props}
          >
            {alert && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={alert.severity}
                  onClose={() => setAlert(null)}
                >
                  {alert.message}
                </Alert>
              </Box>
            )}
            <TextField
              autoFocus
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              label="Email Address"
              margin="normal"
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              type="email"
              value={values.email}
              variant="outlined"
            />
            <PasswordInput
              formControlStyle={{ width: `100%` }}
              style={{ width: `100%` }}
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              label="Password"
              margin="normal"
              name="password"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.password}
              variant="outlined"
            />
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <Button
                color="primary"
                disabled={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                Log In
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mt: 2 }}>
              <Button
                color="primary"
                disabled={microsoftLoading}
                fullWidth
                size="large"
                type="button"
                onClick={handleMicrosoftLogin}
                variant="outlined"
              >
                {microsoftLoading
                  ? "Signing in with Microsoft..."
                  : "Sign in with Microsoft"}
              </Button>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setForgotEmail("");

                  toggleModal();
                }}
              >
                Forgot Password
              </Button>
            </Box>
            {false && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                  <div>
                    Use <b>demo@monger.tech</b> and password <b>monger</b>
                  </div>
                </Alert>
              </Box>
            )}
          </form>
        )}
      </Formik>
      <Dialog open={modalOpen} onClose={toggleModal} toggle={toggleModal}>
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter your email below to recieve a link to reset your password.
          </DialogContentText>
          <TextField
            sx={{ mt: 2 }}
            label="Email"
            onChange={(e) => setForgotEmail(e.target.value)}
            value={forgotEmail}
            type="email"
            fullWidth
          />
        </DialogContent>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DialogActions>
          <Button onClick={toggleModal}>Cancel</Button>
          <Button
            onClick={() => {
              getRecoveryEmail();
            }}
          >
            Recover Password
          </Button>
        </DialogActions>
      </Dialog>
      <TOTPEntryDialog
        loading={OTPLoading}
        error={OTPError}
        open={OTPOpen}
        onClose={handleOTPClose}
        onSubmit={handleOTPSubmit}
      />
    </>
  );
};

export default LoginJWT;
